import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SimpleLayout from '@/layouts/SimpleLayout';
import MaterialIcon from '@/components/MaterialIcon';
import SmartLink from '@/components/SmartLink';
import { useTracking } from '@mygpt/app/utils';

/**
 * NEWoMan高輪 導入事例ページ
 * GBaseSupportSLP の case-study-newoman.html を移植
 */
const NewomanTakanawa: React.FC = () => {
  const { trackSignupClick, trackContactClick } = useTracking();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SimpleLayout>
      <Helmet>
        <title>NEWoMan高輪 導入事例｜GBase SUPPORT</title>
        <meta
          name="description"
          content="NEWoMan高輪でのGBase SUPPORT導入事例。案内所対応最大70%削減、10言語以上の多言語AI案内を実現。商業施設向けAIチャットボット導入の詳細をご紹介。"
        />
        <meta name="keywords" content="NEWoMan高輪, 導入事例, AIチャットボット, 商業施設, 多言語対応, GBase SUPPORT" />
        <meta property="og:image" content="https://cs.gbase.ai/newoman-takanawa-og.png" />
        <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
        <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />
      </Helmet>

      <div className="font-solution bg-white leading-relaxed text-solution-text">
        {/* Hero Section */}
        <section
          className="px-4 md:px-10 py-20"
          style={{ background: 'linear-gradient(135deg, #F3E8FF 0%, #E0E7FF 100%)' }}
        >
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <span className="inline-block bg-[#7C3AED] text-white px-4 py-1.5 rounded-2xl text-sm mb-5">
                NEWoMan高輪
              </span>
              <h1 className="text-2xl md:text-3xl font-bold !leading-normal text-gray-800 mb-5">
                案内所対応<span className="text-[#7C3AED]">最大70%削減</span>！
                <br />
                NEWoMan高輪がGBase SUPPORTで実現した多言語AI案内
              </h1>
              <div className="flex flex-nowrap gap-8 mt-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#7C3AED] mb-2">50%+</div>
                  <div className="text-xs text-gray-500">案内所・電話対応削減</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#7C3AED] mb-2">10+</div>
                  <div className="text-xs text-gray-500">多言語対応(Inbound)</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#7C3AED]">
                    <MaterialIcon name="devices" className="text-[#7C3AED] text-5xl align-middle" />
                  </div>
                  <div className="text-xs text-gray-500">PC・スマホ・大型タッチパネル対応</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#7C3AED] mb-2">1秒</div>
                  <div className="text-xs text-gray-500">以内に即時応答</div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://raw.githubusercontent.com/AilisonC147/GBaseSupportSLP/main/banner.jpg"
                alt="NEWoMan高輪 商業施設"
                className="w-full rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-[900px] mx-auto px-4 md:px-10 py-20">
          {/* 導入企業プロフィール */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">
              導入企業プロフィール
            </h2>
            <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
              <tbody>
                {[
                  ['施設名', 'NEWoMan高輪（ニュウマン高輪）'],
                  ['運営会社', '株式会社ルミネ'],
                  ['開業日', '2025年9月12日'],
                  ['延床面積', '約60,000㎡（South・North・LUFTBAUM・MIMURE含む）'],
                  ['店舗数', '約200店舗'],
                  ['所在地', '東京都港区高輪 TAKANAWA GATEWAY CITY内'],
                ].map(([th, td], i, arr) => (
                  <tr key={i}>
                    <th
                      className={`bg-gray-50 font-semibold w-[150px] px-5 py-4 text-left text-gray-800 ${
                        i < arr.length - 1 ? 'border-b border-gray-200' : ''
                      }`}
                    >
                      {th}
                    </th>
                    <td className={`px-5 py-4 text-gray-700 ${i < arr.length - 1 ? 'border-b border-gray-200' : ''}`}>
                      {td}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* 導入の背景 */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">導入の背景</h2>
            <p className="text-gray-600">
              NEWoMan高輪は、ルミネ史上最大規模の商業施設として、「100年先へ継承する"まだ見ぬ生活価値づくり"」をコンセプトに開業しました。国際玄関口である高輪ゲートウェイに位置し、インバウンド観光客を含む多様なお客様への対応が求められていました。
            </p>
          </section>

          {/* 導入前の課題 */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">導入前の課題</h2>
            <div className="grid gap-5 mt-8">
              {[
                {
                  title: '多言語対応の限界',
                  desc: 'インバウンド観光客からの問い合わせ増加に対し、多言語対応スタッフの確保が困難でした。',
                },
                {
                  title: '案内所の混雑',
                  desc: '広大な施設内での道案内や店舗情報の問い合わせで案内所が常に混雑状態でした。',
                },
                {
                  title: '定型問い合わせによるスタッフリソースの逼迫',
                  desc: '「○○の店はどこ？」「営業時間は？」など繰り返し寄せられる定型的な問い合わせ対応に多くの人的リソースが割かれ、お客様へのきめ細やかな商品案内や高付加価値な接客に集中できていませんでした。',
                },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 p-6 rounded-xl border-l-4 border-[#7C3AED]">
                  <h4 className="text-base font-semibold text-gray-800 mb-3">{item.title}</h4>
                  <p className="text-sm text-gray-500 !mb-0">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* GBase SUPPORTを選んだ理由 */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">
              GBase SUPPORTを選んだ理由
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {[
                {
                  num: 1,
                  title: '専用端末不要、お客様のスマートフォンで利用可能',
                  desc: 'お客様がQRコードを読み込むだけで、広い館内のどこにいても必要な情報にスマートにアクセスできます。高額な専用端末への投資は不要でした。',
                },
                {
                  num: 2,
                  title: '多言語リアルタイム対応',
                  desc: '日本語・英語・中国語・韓国語など10言語以上に自動対応。インバウンド対応が充実しています。',
                },
                {
                  num: 3,
                  title: '独自のRAGシステム',
                  desc: '店舗・施設情報を事前に学習したナレッジベースにより、的確な問い合わせ対応を実現します。',
                },
              ].map((item) => (
                <div
                  key={item.num}
                  className="bg-white border border-gray-200 rounded-2xl py-9 px-7 transition-all duration-300 hover:shadow-lg hover:border-[#A78BFA] hover:-translate-y-1"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-[#7C3AED] to-[#A78BFA] text-white rounded-full flex items-center justify-center font-bold mb-4">
                    {item.num}
                  </div>
                  <h4 className="text-base font-semibold text-gray-800 mb-2.5">{item.title}</h4>
                  <p className="text-sm text-gray-600 !mb-0 leading-7">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 導入システム構成 */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">
              導入システム構成
            </h2>
            <div className="bg-solution-bg border border-solution-border rounded-2xl p-8 mt-8">
              {/* Top: Customer ↔ Staff */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-center">
                {/* Customer card */}
                <div className="bg-white rounded-xl overflow-hidden shadow-md border-[1.5px] border-blue-200">
                  <div
                    className="px-4 py-3 flex items-center gap-3 text-white"
                    style={{ background: 'linear-gradient(135deg, #1D4ED8, #3B82F6)' }}
                  >
                    <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MaterialIcon name="smartphone" className="text-xl" />
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80">お客様側</div>
                      <div className="text-sm font-bold mt-0.5">お客様スマホ</div>
                    </div>
                  </div>
                  <div className="p-4">
                    {[
                      { icon: 'qr_code_scanner', text: 'QRコード読取' },
                      { icon: 'chat_bubble_outline', text: '多言語AI対話' },
                      { icon: 'place', text: '場所案内' },
                    ].map((item, i, arr) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 py-2 text-[13px] font-medium text-solution-text ${
                          i < arr.length - 1 ? 'border-b border-blue-50' : ''
                        }`}
                      >
                        <MaterialIcon name={item.icon} className="text-blue-500 text-[15px] flex-shrink-0" />
                        {item.text}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Connector (hidden on mobile) */}
                <div className="hidden md:flex flex-col items-center gap-1.5 px-5">
                  <style>{`
                    @keyframes sdv-r {
                      0%   { left: 0; opacity: 0; }
                      15%  { opacity: 1; }
                      85%  { opacity: 1; }
                      100% { left: calc(100% - 7px); opacity: 0; }
                    }
                    @keyframes sdv-l {
                      0%   { left: calc(100% - 7px); opacity: 0; }
                      15%  { opacity: 1; }
                      85%  { opacity: 1; }
                      100% { left: 0; opacity: 0; }
                    }
                  `}</style>
                  <div className="w-20 h-0.5 bg-gradient-to-r from-blue-500 to-solution-orange relative">
                    <span
                      className="absolute w-[7px] h-[7px] rounded-full bg-blue-500 top-1/2 -translate-y-1/2"
                      style={{ animation: 'sdv-r 1.8s linear infinite 0s' }}
                    />
                    <span
                      className="absolute w-[7px] h-[7px] rounded-full bg-blue-500 top-1/2 -translate-y-1/2"
                      style={{ animation: 'sdv-r 1.8s linear infinite 0.9s' }}
                    />
                    <span
                      className="absolute w-[7px] h-[7px] rounded-full bg-solution-orange top-1/2 -translate-y-1/2"
                      style={{ animation: 'sdv-l 1.8s linear infinite 0.45s' }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-400 whitespace-nowrap font-medium">リアルタイム連携</span>
                </div>

                {/* Staff card */}
                <div className="bg-white rounded-xl overflow-hidden shadow-md border-[1.5px] border-orange-200">
                  <div
                    className="px-4 py-3 flex items-center gap-3 text-white"
                    style={{ background: 'linear-gradient(135deg, #C2680A, #EE862B)' }}
                  >
                    <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MaterialIcon name="computer" className="text-xl" />
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80">スタッフ側</div>
                      <div className="text-sm font-bold mt-0.5">スタッフ管理画面</div>
                    </div>
                  </div>
                  <div className="p-4">
                    {[
                      { icon: 'history', text: '会話履歴確認' },
                      { icon: 'monitor', text: 'リアルタイムモニタリング' },
                    ].map((item, i, arr) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 py-2 text-[13px] font-medium text-solution-text ${
                          i < arr.length - 1 ? 'border-b border-orange-50' : ''
                        }`}
                      >
                        <MaterialIcon name={item.icon} className="text-solution-orange text-[15px] flex-shrink-0" />
                        {item.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Down arrow */}
              <div className="flex flex-col items-center py-1.5">
                <div className="w-0.5 h-5 bg-gradient-to-b from-solution-border to-[#7C3AED]" />
                <MaterialIcon name="south" className="text-[#7C3AED] text-xl animate-bounce" />
              </div>

              {/* Cloud platform */}
              <div className="bg-white border-[1.5px] border-[#7C3AED] rounded-xl overflow-hidden shadow-lg">
                <div
                  className="px-6 py-4 flex items-center gap-3 text-white"
                  style={{ background: 'linear-gradient(135deg, #5B21B6, #7C3AED)' }}
                >
                  <MaterialIcon name="cloud" className="text-xl text-white/90" />
                  <span className="text-[15px] font-bold">GBase SUPPORT</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4">
                  {[
                    { icon: 'smart_toy', label: '生成AIによる\n自動応答' },
                    { icon: 'translate', label: '多言語翻訳\n（10言語以上）' },
                    { icon: 'storage', label: '店舗情報\nナレッジベース' },
                    { icon: 'map', label: 'フロアマップ\n連携' },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="py-5 px-3.5 flex flex-col items-center text-center gap-2.5 border-r border-purple-100 last:border-r-0 bg-[#FAFAFF]"
                    >
                      <MaterialIcon name={item.icon} className="text-[#7C3AED] text-2xl" />
                      <span className="text-xs text-solution-text font-medium leading-normal whitespace-pre-line">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 主な活用機能 */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">主な活用機能</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {[
                {
                  icon: '📍',
                  title: 'フロアマップ・ナビゲーション',
                  desc: 'お客様の現在地から目的店舗まで、わかりやすいルート案内を提供',
                },
                { icon: '💬', title: 'FAQ・施設案内AI', desc: '店舗情報、営業時間、イベント情報などをAIが自動回答' },
                {
                  icon: '🌐',
                  title: 'リアルタイム多言語会話',
                  desc: '日本語・英語・中国語・韓国語・ベトナム語・ポルトガル語など10言語以上に対応',
                },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-2xl px-7 py-9 text-center">
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h4 className="text-base font-semibold text-gray-800 mb-2.5">{item.title}</h4>
                  <p className="text-[13px] text-gray-600 !mb-0 leading-7">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-gray-600">その他、免税カウンター、外貨両替、Wi-Fi情報なども多言語で案内</p>
          </section>

          {/* 導入効果 */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">導入効果</h2>
            <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-md mt-8">
              <thead>
                <tr>
                  <th className="bg-[#7C3AED] text-white px-5 py-4 text-left font-semibold">指標</th>
                  <th className="bg-[#7C3AED] text-white px-5 py-4 text-left font-semibold">効果</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['案内所対応', '50%+削減'],
                  ['AI応答速度', '1秒以内'],
                  ['多言語対応', '10言語以上'],
                  ['対応時間', '24時間365日'],
                ].map(([label, value], i, arr) => (
                  <tr key={i}>
                    <td className={`px-5 py-4 ${i < arr.length - 1 ? 'border-b border-gray-200' : ''}`}>{label}</td>
                    <td
                      className={`px-5 py-4 font-bold text-[#7C3AED] ${
                        i < arr.length - 1 ? 'border-b border-gray-200' : ''
                      }`}
                    >
                      {value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* お客様の声 */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">お客様の声</h2>
            <div
              className="p-10 rounded-2xl relative"
              style={{ background: 'linear-gradient(135deg, #F3E8FF 0%, #E0E7FF 100%)' }}
            >
              <span className="absolute top-2 left-5 text-8xl text-[#A78BFA] font-serif leading-none select-none">
                &ldquo;
              </span>
              <p className="text-base leading-loose text-gray-800 pl-10 mb-5">
                導入初月から効果を実感しました。案内所への問い合わせが大幅に減少し、スタッフは接客に集中できるようになりました。特にインバウンド対応では、多言語AIが活躍しています。
              </p>
              <p className="text-right text-sm text-gray-500 !mb-0">— NEWoMan高輪 運営担当者</p>
            </div>
          </section>

          {/* NEWoMan高輪の「人×デジタル」戦略 */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">
              NEWoMan高輪の「人×デジタル」戦略
            </h2>
            <p className="text-gray-500">
              NEWoMan高輪では、GBase
              SUPPORTによるAI案内を導入しつつも、「人にしかできないおもてなし」を大切にしています。定型的な問い合わせはAIが対応し、複雑なご要望や特別なサービスは、スタッフが心を込めて対応。デジタルと人の力を組み合わせることで、すべてのお客様に最高の体験を提供しています。
            </p>
          </section>

          {/* 今後の展望 */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold mb-8 pb-4 border-b-[3px] border-[#7C3AED] inline-block">今後の展望</h2>
            <p className="text-gray-500">
              NEWoMan高輪では、GBase SUPPORTの活用をさらに発展させ、より高度な顧客体験の実現を目指しています。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {[
                {
                  num: 1,
                  title: '商品検索AIの展開',
                  desc: '「赤いワンピースはありますか？」といったカテゴリ・カラーによる曖昧検索にも対応する商品検索AIの導入を検討しています。POS連携によるリアルタイム在庫照会と組み合わせることで、お客様の購買体験をさらに向上させます。',
                },
                {
                  num: 2,
                  title: 'LINE公式アカウント連携の展開',
                  desc: '来館前からのお問い合わせ対応を強化するため、LINE公式アカウントとの連携を予定しています。プッシュ通知によるイベント案内やクーポン配信を通じて、リピーター獲得と継続的な顧客接点の構築を目指します。',
                },
                {
                  num: 3,
                  title: '有人転送・スタッフ連携の強化',
                  desc: 'AIで対応困難なケースや特別なご要望に対し、スタッフへのシームレスな転送フローをさらに最適化。「AIと人の協働」による最高のおもてなしを追求します。',
                },
              ].map((item) => (
                <div
                  key={item.num}
                  className="bg-white border border-gray-200 rounded-2xl px-7 py-9 transition-all duration-300 hover:shadow-lg hover:border-[#A78BFA] hover:-translate-y-1"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-[#7C3AED] to-[#A78BFA] text-white rounded-full flex items-center justify-center font-bold mb-4">
                    {item.num}
                  </div>
                  <h4 className="text-base font-semibold text-gray-800 mb-2.5">{item.title}</h4>
                  <p className="text-sm text-gray-600 !mb-0 leading-7">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-gray-800 text-white py-16 px-10 text-center rounded-3xl my-16">
            <h2 className="text-2xl font-bold mb-5 border-b-[3px] border-[#A78BFA] inline-block pb-4 text-white">
              お問い合わせ
            </h2>
            <p className="text-white/80 mb-8">GBase SUPPORTについての詳細は、以下よりお問い合わせください。</p>
            <div className="flex flex-wrap gap-5 justify-center">
              <SmartLink
                to="/register"
                className="inline-flex items-center gap-2 h-11 px-7 rounded-full text-[15px] font-semibold border-[1.5px] border-white/35 text-white hover:border-white transition-colors"
                onClick={() => trackSignupClick('case_study_newoman')}
              >
                14日間無料トライアル
              </SmartLink>
              <SmartLink
                to="/contact"
                className="inline-flex items-center gap-2 h-11 px-7 rounded-full text-[15px] font-semibold text-white bg-gradient-to-r from-blue-500 to-grape-500 hover:from-blue-600 hover:to-grape-600 transition-all transition-opacity hover:opacity-90"
                onClick={() => trackContactClick('case_study_newoman')}
              >
                お問い合わせ
              </SmartLink>
            </div>
          </section>
        </div>
      </div>
    </SimpleLayout>
  );
};

export default NewomanTakanawa;
